import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { ArrowLeft, Bug, Lock, RefreshCw, WifiOff } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ForbiddenError } from "@/lib/forbiddenError";
import { captureError } from "@/sentry";

type RouteErrorPanelProps = {
	error: Error;
	/** TanStack Router's error-boundary reset, passed by `errorComponent`. */
	reset?: () => void;
};

/** HTTP status off an axios-shaped rejection, or undefined for non-HTTP errors. */
function errorStatus(error: unknown): number | undefined {
	const status = (error as { response?: { status?: unknown } } | null)?.response
		?.status;
	return typeof status === "number" ? status : undefined;
}

/**
 * In-shell route error boundary: renders inside the matched route's slot so
 * the sidebar/nav stay usable, states plainly that loading failed (never an
 * entity-empty message), and retries by resetting the boundary, clearing the
 * errored queries and re-running the route loaders.
 */
export function RouteErrorPanel({ error, reset }: RouteErrorPanelProps) {
	const { t } = useTranslation();
	const router = useRouter();
	const queryClient = useQueryClient();
	const reportedRef = useRef(false);

	// A permission denial (loader guard or HTTP 403) is not a transient
	// failure: say so, don't offer a Retry that can never succeed, and don't
	// report it as an error.
	const forbidden =
		error instanceof ForbiddenError || errorStatus(error) === 403;
	const networkError = isAxiosError(error) && !error.response;
	const titleKey = forbidden ? "errors.forbidden_Title" : "common.loadFailed";
	const messageKey = forbidden
		? "errors.forbidden_Message"
		: networkError
			? "common.loadFailedDescription"
			: "errors.generic_Message";

	useEffect(() => {
		if (reportedRef.current || forbidden) return;
		reportedRef.current = true;
		captureError(error, { tags: { component: "RouteErrorPanel" } });
	}, [error, forbidden]);

	const handleRetry = () => {
		// Errored queries hold no data, so resetting them forces a refetch when
		// the loaders re-run; reset() clears the router error boundary itself.
		queryClient.resetQueries({ predicate: (q) => q.state.status === "error" });
		reset?.();
		router.invalidate();
	};

	return (
		<div
			role="alert"
			className="flex h-full min-h-[50vh] flex-col items-center justify-center px-4 py-12 text-center"
		>
			<div className="mb-4 rounded-full bg-warning/10 p-4">
				{forbidden ? (
					<Lock className="h-8 w-8 text-warning" aria-hidden />
				) : networkError ? (
					<WifiOff className="h-8 w-8 text-warning" aria-hidden />
				) : (
					<Bug className="h-8 w-8 text-warning" aria-hidden />
				)}
			</div>
			<h2 className="mb-1 text-lg font-semibold text-base-content">
				{t(titleKey)}
			</h2>
			<p className="mb-6 max-w-sm text-sm text-base-content/70">
				{t(messageKey)}
			</p>
			<div className="flex flex-wrap items-center justify-center gap-2">
				{!forbidden && (
					<Button onClick={handleRetry}>
						<RefreshCw className="mr-2 h-4 w-4" aria-hidden />
						{t("common.retry")}
					</Button>
				)}
				<Button variant="outline" onClick={() => router.history.back()}>
					<ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
					{t("common.back")}
				</Button>
			</div>
		</div>
	);
}
